<script setup>
defineOptions({
    name: 'ContractorSelectionModal',
});

/*
|--------------------------------------------------------------------------
| ContractorSelectionModal.vue
|--------------------------------------------------------------------------
| Purpose
|   Modal for selecting a contractor from the "Contactpersonen" eligible list.
|   Used by the dashboard tile to navigate to the contacts page.
|
| Usage (parent):
|   <ContractorSelectionModal v-model="showModal" @select="onSelect" />
|
| Props
|   - modelValue (Boolean): controls modal visibility via v-model
|
| Events
|   - update:modelValue(Boolean): emitted when the modal opens/closes
|   - select(Object): emitted when the user chooses a contractor
|   - close: emitted when modal is closed
|
| Workflow
|   1) Setup & routing helper
|   2) Props & emits
|   3) Reactive state
|   4) Methods: handleSelect, close
*/

import { ref, computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Modal from '@/components/Modal.vue';
import FormRichSelect from '@/components/Form/FormRichSelect.vue';

/* Routing helper (Ziggy route) */
const instance = getCurrentInstance();
const route = instance?.appContext.config.globalProperties.route;

/* Props & Emits */
const props = defineProps({
    /** Controls modal visibility - accepts boolean or truthy value (e.g. tile key) */
    modelValue: { type: [Boolean, String], default: false },
    tile: { type: Object, default: null },
    selectionMode: {
        type: String,
        default: 'navigate',
        validator: (value) => ['navigate', 'emit_only'].includes(value),
    },
});

const emit = defineEmits(['update:modelValue', 'select', 'close']);

/* v-model adapter for modal visibility - converts to boolean for Modal component */
const show = computed({
    get: () => !!props.modelValue,
    set: (val) => emit('update:modelValue', val),
});

/* Reactive state */
const selectedContractor = ref(null);

/* Search URL for FormRichSelect remote search */
const searchUrl = computed(() => route('contractors.search.ajax'));

/**
 * Handle contractor selection
 * Redirects to contacts page for selected contractor
 *
 * @route GET /contractors/{contractor}/contacts
 */
function handleSelect() {
    if (!selectedContractor.value) return;

    const contractorSelection = getContractorSelection(selectedContractor.value);
    if (!contractorSelection.id) return;

    emit('select', {
        contractor_id: contractorSelection.id,
        contractor_name: contractorSelection.name,
    });

    if (props.selectionMode === 'navigate') {
        const url = route('contractors.contacts', { contractor: contractorSelection.id });
        router.visit(url);
    }

    close();
}

function getContractorSelection(selectedValue) {
    if (selectedValue && typeof selectedValue === 'object') {
        return {
            id: selectedValue.id ?? selectedValue.value ?? null,
            name: selectedValue.label ?? selectedValue.name ?? '',
        };
    }

    return {
        id: selectedValue,
        name: '',
    };
}

/**
 * Close the modal and reset state
 */
function close() {
    show.value = false;
    selectedContractor.value = null;
    emit('close');
}
</script>

<template>
    <Modal
        :show="show"
        title="Contactpersonen"
        size="2xl"
        theme="orange"
        confirm-button-variant="orange"
        :confirm-button-disabled="!selectedContractor"
        :show-default-footer="true"
        @close="close"
        @cancel="close"
        @confirm="handleSelect"
        :cancel-button-id="'input-contractor-modal-cancel'"
        :confirm-button-id="'input-contractor-modal-select'"
    >
        <div class="contractor-selection-modal-content">
            <!-- Description -->
            <p class="modal-description">Selecteer een opdrachtnemer om de contactpersonen toe te voegen.</p>

            <!-- Contractor Dropdown -->
            <div class="contractor-selection-field">
                <label for="input-contractor-selection" class="form-label"> Opdrachtnemer </label>
                <FormRichSelect
                    id="input-contractor-selection"
                    v-model="selectedContractor"
                    :search-url="searchUrl"
                    placeholder="Zoek op naam..."
                    option-label="label"
                    option-value="value"
                    return-object
                />
            </div>
        </div>
    </Modal>
</template>
